import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { formatDateTime } from "@/lib/utils";

export default function ReportsPage() {
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    api.get("/jobs?status=completed").then(({ data }) => setJobs(data));
  }, []);

  if (!jobs) return <LoadingScreen />;

  return (
    <div className="space-y-6" data-testid="user-reports">
      <div className="sqit-card border-[#0B6936]/20 bg-[#0B6936]/5 p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0B6936] text-white">
            <FileText size={18} />
          </div>
          <div>
            <h3 className="font-display font-semibold text-gray-900">Your completed reports</h3>
            <p className="text-sm text-gray-600">
              All reports and downloadable artefacts from successfully completed jobs.
            </p>
          </div>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="sqit-card p-14 text-center">
          <FileText className="mx-auto text-gray-300" size={36} />
          <p className="mt-4 text-gray-500">You don't have any completed reports yet.</p>
          <Link to="/app/jobs/new"
            className="mt-4 inline-block rounded-md bg-[#0B6936] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#07522f]">
            Submit a job
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((j) => {
            const hasReport = !!j.result?.report_url;
            const files = j.result?.file_urls || [];
            return (
              <div key={j.id} className="sqit-card p-6" data-testid={`report-job-${j.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div>
                    <Link to={`/app/jobs/${j.id}`}
                      className="font-display text-lg font-semibold text-gray-900 transition-colors hover:text-[#0B6936]">
                      {j.job_code}
                    </Link>
                    <div className="text-sm text-gray-600">{j.project_name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {j.module_name} · {j.number_of_samples} samples · Completed {formatDateTime(j.updated_at)}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                    {hasReport && (
                      <a href={j.result.report_url} target="_blank" rel="noreferrer"
                        data-testid={`report-download-${j.id}`}
                        className="inline-flex items-center gap-2 rounded-md bg-[#0B6936] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#07522f]">
                        <Download size={14} /> Report
                      </a>
                    )}
                    <Link to={`/app/jobs/${j.id}`}
                      className="inline-flex items-center gap-1 border border-gray-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-gray-50">
                      Details <ExternalLink size={12} />
                    </Link>
                  </div>
                </div>

                {files.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                      Artefact files ({files.length})
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {files.map((f, i) => (
                        <a key={i} href={f.url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 rounded-md border border-[#E5E7EB] px-3 py-2 text-sm text-gray-700 transition-colors hover:border-[#0B6936]/30 hover:bg-[#0B6936]/5">
                          <Download size={14} className="text-[#0B6936]" />
                          <span className="truncate">{f.name || f.url}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {!hasReport && files.length === 0 && (
                  <p className="mt-4 pt-4 border-t border-[#E5E7EB] text-xs text-gray-500">
                    Report artefacts will appear here once uploaded by our team.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
